import axios from 'axios';
import Config from '../Config';
import MentalApi from './MentalApi';
import FavoritesCache from './FavoritesCache';
import {
    Identifier,
    Favorites,
    FavoriteSection,
    FavoriteItem,
    LessonLinkWithTitle,
} from '../types';

class FavoritesApi {
    private static addLessonIdParameter(baseUrl: string, lessonId: Identifier) {
        return `${baseUrl}&lesson_id=${lessonId}`;
    }

    static async setFavorite(lessonId: Identifier, isFavorit: boolean): Promise<void> {
        const oldCachedState = FavoritesCache.getFavoriteStatus(lessonId);
        FavoritesCache.setFavorite(lessonId, isFavorit);

        try {
            const baseUrl = isFavorit ? Config.addFavoriteUrl : Config.delFavoriteUrl;
            const url = FavoritesApi.addLessonIdParameter(baseUrl, lessonId);

            const result = await axios.get(url);

            const expectedResponse = isFavorit ? 'favorite saved' : 'favorite deleted';
            if (result.data.response !== expectedResponse) {
                throw new Error('Unexpected response');
            }
        } catch (e) {
            FavoritesCache.setFavorite(lessonId, oldCachedState);
            throw e;
        }
    }

    static async addFavorite(lessonId: Identifier): Promise<void> {
        return this.setFavorite(lessonId, true);
    }

    static async deleteFavorite(lessonId: Identifier): Promise<void> {
        return this.setFavorite(lessonId, false);
    }

    static async getFavorites(): Promise<Favorites> {
        const apiResult = await axios.get(Config.getFavoritesUrl);
        const { response } = apiResult.data;

        const sections = (response.section || []).map((section: any): FavoriteSection => {
            const items: Array<FavoriteItem> = (section.lessons || []).map((lessonData: any) => {
                const id = lessonData.lesson_id;
                const title = lessonData.lesson_title;
                const lessonLink : LessonLinkWithTitle | undefined = MentalApi.findLessonById(id);

                return {
                    id,
                    title,
                    lessonLink,
                };
            })
                .filter((tmpItem: any) => tmpItem.lessonLink)
                .map((tmpItem: any): FavoriteItem => ({
                    link: {
                        pathname: tmpItem.lessonLink.link,
                        state: {
                            fromFavorites: true,
                        },
                    },
                    title: tmpItem.title,
                    lessonId: tmpItem.id,
                }));

            return {
                title: section.mental_titel,
                itemIconColor: section.fav_icon_color,
                items,
            };
        });

        return {
            pageLayout: {
                headerColor: {
                    bgColor: response.layout.header_bg_color,
                    fontColor: response.layout.header_font_color,
                },
                bodyColor: {
                    bgColor: response.layout.body_bg_color,
                    fontColor: response.layout.uebungen_font_color,
                },
                headerWaveUrl: response.layout.header_welle,
            },
            sectionHeaderColor: response.layout.rubriken_font_color,
            sections,
        };
    }
}

export default FavoritesApi;
